// Login screen (Phase 5 D-A — real /api/v1/login flow).
//
// Replaces the Phase 4.5 D7 placeholder ("paste a token") with a
// real username + password form that calls the app process's
// POST /api/v1/login endpoint. On 200, the server sets the
// HttpOnly + Secure + SameSite=Strict session cookie; the UI
// transitions to the dashboard via the onLogin callback.
//
// Error handling:
//   * 401 → "Invalid username or password" (anti-enumeration —
//     same message for unknown user vs wrong password).
//   * 423 → "Account locked, try again at HH:MM" (the server's
//     RFC 7807 detail field carries the unlock time).
//   * Network/other → generic error message.
//
// The Phase 4.5 D7 setSessionToken() flow is preserved as a
// fallback when running in fixture mode (no backend).
//
// Accessibility: form is labelled, error has role="alert", the
// submit button is disabled while the request is in flight.

import { useState } from "react";
import { ApiClient, ProblemDetailError } from "../api/client";
import { useFixtures } from "../api/dataSource";
import { setSessionToken } from "../lib/session";

type Props = {
  onLogin: () => void;
  apiClient?: ApiClient;
};

const FIXTURE_USER = "fixture-mode";

export function LoginScreen({ onLogin, apiClient }: Props): JSX.Element {
  const fixturesEnabled = useFixtures();
  const client = apiClient ?? new ApiClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);

    // Fixture mode: the UI is wired against fixtures, no backend
    // is reachable. Accept any non-empty username/password and
    // set a placeholder client-side cookie so the rest of the UI
    // sees a "logged in" state.
    if (fixturesEnabled) {
      if (username.trim() === "" || password === "") {
        setError("Username and password required.");
        setBusy(false);
        return;
      }
      setSessionToken(`${FIXTURE_USER}-${Date.now()}`);
      setBusy(false);
      onLogin();
      return;
    }

    try {
      await client.login({
        username: username.trim(),
        password,
      });
      // The server set the cookie via Set-Cookie; we don't
      // touch document.cookie here.
      setBusy(false);
      onLogin();
    } catch (err) {
      setBusy(false);
      if (err instanceof ProblemDetailError) {
        if (err.status === 401) {
          setError("Invalid username or password.");
        } else if (err.status === 423) {
          // Detail typically reads:
          //   "Account is locked until 2026-05-05T13:30:00+00:00."
          setError(err.detail || "Account locked.");
        } else {
          setError(err.detail || err.title);
        }
        return;
      }
      setError("Login failed. Check the network and try again.");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "1rem",
      }}
    >
      <h1>tradingplatform</h1>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          width: "320px",
        }}
      >
        <label htmlFor="login-username" style={{ fontSize: "0.85rem" }}>
          Username
          <input
            id="login-username"
            type="text"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={{
              width: "100%",
              padding: "0.5rem",
              marginTop: "0.25rem",
            }}
          />
        </label>
        <label htmlFor="login-password" style={{ fontSize: "0.85rem" }}>
          Password
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: "100%",
              padding: "0.5rem",
              marginTop: "0.25rem",
            }}
          />
        </label>
        {error && (
          <div
            role="alert"
            style={{
              color: "#c33",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          style={{ padding: "0.5rem" }}
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {fixturesEnabled && (
          <p
            style={{
              fontSize: "0.7rem",
              color: "#888",
              margin: 0,
            }}
          >
            Fixture mode: any non-empty username + password is
            accepted.
          </p>
        )}
      </form>
    </div>
  );
}
